import { icon } from "./iconsKit";

/** Tiny DOM builders for the UI kit classes (tokens.css). Markup only — no game rules here. */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", html = ""): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (html) e.innerHTML = html;
  return e;
}

export type PillTint = "" | "gold" | "blue" | "green";

/** Resource / counter pill: icon disc, number, optional green plus. */
/** `iconName` is a kit icon name, or an image URL (`./`, `/`, `data:`, `http`) for a painted icon such as the HQ resource renders. */
export function pillHtml(iconName: string, value: string | number, tint: PillTint = "", plus = false, extraClass = ""): string {
  const ic = /^(\.|\/|data:|blob:|https?:)/.test(iconName) ? `<img decoding="sync" src="${iconName}" alt="">` : icon(iconName, 14, "#3a2410", 2.6);
  return `<div class="pill ${extraClass}"><div class="ic ${tint}${ic.startsWith("<img") ? " img" : ""}">${ic}</div><span>${value}</span>${plus ? `<span class="plus">+</span>` : ""}</div>`;
}

/** `danger` is the red destructive style (Salvage, ERASE, QUIT): gold stays the "go ahead" colour. */
export function btnHtml(label: string, variant: "gold" | "blue" | "dark" | "danger", opts: { icon?: string; iconRight?: string; className?: string; disabled?: boolean; sub?: string; data?: string } = {}): string {
  const sub = opts.sub ? `<span class="sub">${opts.sub}</span>` : "";
  const inner = `${opts.icon ? icon(opts.icon, 18) : ""}<span class="lbl"><span class="disp">${label}</span>${sub}</span>${opts.iconRight ? icon(opts.iconRight, 18) : ""}`;
  return `<button class="btn ${variant} ${opts.className ?? ""}${opts.disabled ? " dis" : ""}" ${opts.disabled ? "disabled" : ""} ${opts.data ?? ""}>${inner}</button>`;
}

/** Apple's minimum comfortable tap target (HIG 44 pt): sheet close buttons and other icon buttons outside the HUD. */
export const TAP_MIN = 44;

/** The close (×) icon button on a sheet: the kit icbtn at the minimum tap size. */
export function closeBtnHtml(): string { return icbtnHtml("close", "close", "", null, TAP_MIN); }

/**
 * What VoiceOver says for an icon button that has no visible label, by its icon: the icon alone is not a name, and a
 * nameless button reads as "button".
 */
const ICON_NAMES: Record<string, string> = {
  close: "Close", back: "Back", gear: "Settings", map: "Whole map", turn_left: "Turn the map left", turn_right: "Turn the map right",
  pause: "Pause", play: "Play", bag: "Gear", info: "Info", chev: "More",
};

/** The accessible name for an icon button: its visible label, else its icon's name, else the icon's own name spelled out. */
export function iconButtonName(iconName: string, label = ""): string {
  return label || ICON_NAMES[iconName] || iconName.replace(/_/g, " ");
}

export function icbtnHtml(iconName: string, className = "", label = "", badge: number | string | null = null, size = 48): string {
  return `<button class="icbtn ${className}" aria-label="${iconButtonName(iconName, label)}" style="${size !== 48 ? `width:${size}px;height:${size}px;border-radius:${Math.round(size * 0.28)}px` : ""}">${icon(iconName, Math.round(size * 0.46))}${label ? `<span class="lbl">${label}</span>` : ""}${badge !== null && badge !== 0 ? `<span class="dot">${badge}</span>` : ""}</button>`;
}

export function ribbonHtml(text: string, variant: "" | "red" | "blue" = "", className = ""): string {
  return `<div class="ribbon disp ${variant} ${className}">${text}</div>`;
}

export function statHtml(label: string, value: string | number): string {
  return `<div class="stat"><small>${label}</small><b class="disp">${value}</b></div>`;
}

export function barHtml(fraction: number, variant: "" | "red" | "gold" = "", className = ""): string {
  const pct = Math.max(0, Math.min(100, fraction * 100));
  return `<div class="bar ${className}"><div class="fill ${variant}" style="width:${pct}%"></div></div>`;
}

export function starsHtml(earned: number, total = 3, size = 18): string {
  let out = "";
  const filled = icon("star", size, undefined, 2).replace('fill="none"', 'fill="currentColor"');
  const empty = icon("star", size, undefined, 2);
  for (let i = 0; i < total; i++) out += `<span class="star ${i < earned ? "" : "off"}">${i < earned ? filled : empty}</span>`;
  return out;
}

/**
 * Segmented control, the kit's one style (`.seg2`, sheets.css): a `tablist` of `tab` buttons, the active one `.on` and
 * `aria-selected`, only it in the Tab order (the arrow keys move between them, see bindSeg). `label` names the group
 * for VoiceOver ("Target priority").
 */
export function segHtml(options: readonly { key: string; label: string }[], activeKey: string, label = ""): string {
  const tab = (o: { key: string; label: string }) => {
    const on = o.key === activeKey;
    return `<button type="button" role="tab" class="sg${on ? " on" : ""}" data-k="${o.key}" aria-selected="${on}" tabindex="${on ? 0 : -1}">${o.label}</button>`;
  };
  return `<div class="seg2 kseg" role="tablist"${label ? ` aria-label="${label}"` : ""}>${options.map(tab).join("")}</div>`;
}

/**
 * Wires every segHtml control under `root`: a tap, or ←/→/Home/End on the focused tab, picks that key. `pick` usually
 * redraws, so focus goes back to the newly chosen tab afterwards (found again under `root`) and a keyboard or switch
 * user keeps their place.
 */
export function bindSeg(root: ParentNode, pick: (key: string) => void): void {
  const choose = (key: string, refocus: boolean): void => {
    pick(key);
    if (refocus) root.querySelector<HTMLElement>(`.kseg [role=tab][data-k="${key}"]`)?.focus();
  };
  for (const seg of root.querySelectorAll<HTMLElement>(".kseg")) {
    const tabs = [...seg.querySelectorAll<HTMLButtonElement>("[role=tab]")];
    tabs.forEach((b, i) => {
      b.onclick = () => choose(b.dataset.k ?? "", false);
      b.onkeydown = (e) => {
        const to = e.key === "ArrowRight" ? i + 1 : e.key === "ArrowLeft" ? i - 1 : e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : null;
        if (to === null) return;
        e.preventDefault();
        choose(tabs[(to + tabs.length) % tabs.length].dataset.k ?? "", true);
      };
    });
  }
}

/* ---------------------------------------------------------------------------------------------------------------
 * Juice helpers (art wow pass, ui.md F4/F5/F10). Pure maths first (unit-tested in test/uiJuice.test.ts, no DOM),
 * then the DOM bits. Every animation here touches `transform`/`opacity` only — WKWebView composites those on the GPU,
 * while width, box-shadow or filter animations repaint the element every frame.
 * ------------------------------------------------------------------------------------------------------------- */

/** easeOutCubic: fast start, soft landing — a counter that rolls quickly and settles on its value. */
export function easeOutCubic(t: number): number {
  const u = 1 - Math.max(0, Math.min(1, t));
  return 1 - u * u * u;
}

/** The integer a counter ticking `from → to` shows at `elapsed` of `ms`. Rounds toward `from` so it never overshoots. */
export function tickValueAt(from: number, to: number, elapsed: number, ms: number): number {
  if (ms <= 0 || elapsed >= ms) return to;
  const v = from + (to - from) * easeOutCubic(elapsed / ms);
  return to >= from ? Math.floor(v) : Math.ceil(v);
}

/** Counter text: thousands get a thin grouping comma (12,450) so a rolling number keeps its shape. */
export function fmtCount(v: number): string {
  const n = Math.round(v);
  return Math.abs(n) < 10000 ? String(n) : n.toLocaleString("en-US");
}

/** Below this a pill shows the exact number (4 digits fit the pill); from here it switches to K/M. */
const COMPACT_FROM = 10000;

/**
 * Resource-pill text: exact up to 9999 (the same as `fmtCount`, so a cost can be read off to the unit), then three
 * significant figures with a K/M suffix — 12.3K, 123K, 1.23M — so a pill never needs more than five characters and fits
 * its half of the hub top bar at 375 px. Rounds down, so a pill never shows more than the player has.
 */
export function fmtCompact(v: number): string {
  const n = Math.round(v);
  const a = Math.abs(n);
  if (a < COMPACT_FROM) return n.toLocaleString("en-US");   // owner 2026-10-21: "4,000" like "1,294", never "4000"
  const [div, suffix] = a < 1e6 ? [1e3, "K"] : a < 1e9 ? [1e6, "M"] : [1e9, "B"];
  const scaled = a / div;
  const decimals = scaled < 10 ? 2 : scaled < 100 ? 1 : 0;
  const f = 10 ** decimals;
  const fixed = (Math.floor(scaled * f + 1e-9) / f).toFixed(decimals);          // + ε: 1.23 × 100 is 122.999…
  const text = decimals > 0 ? fixed.replace(/\.?0+$/, "") : fixed;              // 12.0K → 12K, but 100K stays 100K
  return `${n < 0 ? "-" : ""}${text}${suffix}`;
}

/** True when motion is welcome: not the reduced-effects setting (`body.reduced-vfx`) and not the OS reduce-motion. */
export function motionOk(): boolean {
  if (typeof document !== "undefined" && document.body?.classList.contains("reduced-vfx")) return false;
  return !(typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
}

const ticks = new WeakMap<Element, number>();

/**
 * Rolls `el`'s text from `from` to `to` over `ms` (rAF, easeOutCubic). A new tick on the same element cancels the old
 * one, so a counter re-targeted mid-roll just continues from what it shows. The element should use
 * `font-variant-numeric: tabular-nums` (the `.tnum` class) so the digits don't jitter the layout. Returns the value the
 * element will end on. Reduced motion writes the final value at once.
 */
export function tickNumber(el: Element, from: number, to: number, ms = 450, fmt: (v: number) => string = fmtCount): number {
  const prev = ticks.get(el);
  if (prev !== undefined) cancelAnimationFrame(prev);
  ticks.delete(el);
  if (from === to || ms <= 0 || !motionOk() || typeof requestAnimationFrame !== "function") { el.textContent = fmt(to); return to; }
  const t0 = performance.now();
  let last = NaN;
  const step = (t: number) => {
    const v = tickValueAt(from, to, t - t0, ms);
    if (v !== last) { last = v; el.textContent = fmt(v); }        // only touch the DOM when the digit changes
    if (v === to && t - t0 >= ms) { ticks.delete(el); return; }
    ticks.set(el, requestAnimationFrame(step));
  };
  el.textContent = fmt(from);
  ticks.set(el, requestAnimationFrame(step));
  return to;
}

/**
 * A 1.18× pop (220 ms) on a pill or counter, via the Web Animations API so it needs no class juggling or reflow to
 * retrigger. Uses the individual `scale` property so it composes with whatever `transform` the element already has.
 */
export function bump(el: Element | null, peak = 1.18, ms = 220): void {
  if (!el || !motionOk() || typeof (el as HTMLElement).animate !== "function") return;
  (el as HTMLElement).animate([{ scale: "1" }, { scale: String(peak), offset: 0.35 }, { scale: "1" }], { duration: ms, easing: "ease-out" });
}

const floaters = new WeakMap<Element, { el: HTMLElement; sum: number; until: number }>();

/**
 * A green `+n` that rises 18 px and fades above `host` (one pooled node per host). Gains that land while the last one
 * is still up add to it (`+4` then `+6` shows `+10`), so a burst of kills reads as one number instead of a stack.
 */
export function plusFloater(host: HTMLElement, n: number, text = (s: number) => `+${fmtCount(s)}`): void {
  if (n <= 0 || !motionOk()) return;
  let f = floaters.get(host);
  if (!f) {
    const el = document.createElement("span");
    el.className = "plusfloat disp";
    host.append(el);
    f = { el, sum: 0, until: 0 };
    floaters.set(host, f);
  }
  const now = performance.now();
  f.sum = now < f.until ? f.sum + n : n;
  f.until = now + 650;
  f.el.textContent = text(f.sum);
  if (typeof f.el.animate === "function") {
    f.el.getAnimations?.().forEach((a) => a.cancel());
    f.el.animate([{ opacity: 0, transform: "translate(-50%, 4px)" }, { opacity: 1, transform: "translate(-50%, -6px)", offset: 0.2 }, { opacity: 1, transform: "translate(-50%, -14px)", offset: 0.7 }, { opacity: 0, transform: "translate(-50%, -18px)" }], { duration: 700, easing: "ease-out", fill: "forwards" });
  }
}

/** One cell of the HUD stage bar. */
export interface StageSegment { state: "done" | "current" | "todo"; boss: boolean; groupStart: boolean }

/**
 * Stage-progress bar cells (ui.md F10): one cell per stage, grouped in fives with a wider gap so "stage 13 of 20"
 * reads at a glance, and a group break before every boss stage so the boss cell stands apart. `stage` is the stage in
 * play (0 before the first wave) and `cleared` how many are won: cleared cells are done, the one being fought is
 * current, the rest are to do.
 */
export function stageSegments(stage: number, cleared: number, stages: number, bossStages: readonly number[] = [stages], groupSize = 5): StageSegment[] {
  const out: StageSegment[] = [];
  const boss = new Set(bossStages);
  let run = 0;
  for (let s = 1; s <= stages; s++) {
    const isBoss = boss.has(s);
    const groupStart = s > 1 && (run >= groupSize || isBoss || boss.has(s - 1));
    if (groupStart || s === 1) run = 0;
    run++;
    out.push({ state: s <= cleared ? "done" : s === stage ? "current" : "todo", boss: isBoss, groupStart });
  }
  return out;
}
